import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  Shield, ChevronRight, AlertTriangle, CheckCircle2, ArrowRight, 
  Download, Share2, Mail, ExternalLink, Activity, Target, 
  FileText, Globe, Calendar, User, Hash, Lock, Server, 
  Database, Zap, Clock, ShieldCheck, BarChart3, AlertCircle 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { BUSINESS_NAME, COMPANY_DETAILS } from '@/src/constants';
import { cn } from '@/src/lib/utils';

interface AuditData {
  id: string;
  clientName: string;
  riskScore: number;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  executiveSummary: string;
  findings: Array<{
    title: string;
    severity: 'Low' | 'Medium' | 'High' | 'Critical';
    description: string;
    impact: string;
  }>;
  businessImpact: {
    financial: string;
    operational: string;
    compliance: string;
    reputational: string;
  };
  remediationRoadmap: {
    immediate: string[];
    shortTerm: string[];
    longTerm: string[];
  };
  frameworkAlignment: {
    pciDetails: string;
    hipaaDetails: string;
    soc2Details: string;
  };
  nextSteps: string;
  advisoryNote: string;
  upsellServices: string[];
  generatedAt: string;
  frameworkScores?: {
    pci: number;
    hipaa: number;
    soc2: number;
    iso: number;
  };
}

export default function AuditReport() {
  const { id } = useParams();
  const [data, setData] = useState<AuditData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const res = await fetch(`/api/audits/${id}`);
        if (res.ok) {
          const audit = await res.json();
          // Add mock framework scores if not present for visual depth
          if (!audit.frameworkScores) {
            audit.frameworkScores = {
              pci: Math.max(0, 100 - audit.riskScore - 10),
              hipaa: Math.max(0, 100 - audit.riskScore - 5),
              soc2: Math.max(0, 100 - audit.riskScore - 15),
              iso: Math.max(0, 100 - audit.riskScore - 20)
            };
          }
          setData(audit);
        }
      } catch (error) {
        console.error("Failed to fetch audit:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAudit();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040a] pt-32 pb-20 flex items-center justify-center">
        <div className="container mx-auto px-6 max-w-4xl space-y-8">
          <Skeleton className="h-12 w-64 bg-white/5" />
          <Skeleton className="h-96 w-full bg-white/5 rounded-[3rem]" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-40 bg-white/5 rounded-2xl" />
            <Skeleton className="h-40 bg-white/5 rounded-2xl" />
            <Skeleton className="h-40 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#02040a] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-6 border border-white/10">
          <AlertTriangle className="w-10 h-10 text-accent/50" />
        </div>
        <h1 className="text-2xl font-display font-bold mb-2 tracking-tight">Report Unavailable</h1>
        <p className="text-muted-foreground mb-8 max-w-sm">This consulting deliverable could not be retrieved. Please contact your account manager if this persists.</p>
        <Button asChild className="rounded-xl px-8">
          <Link to="/">Back to Portal</Link>
        </Button>
      </div>
    );
  }

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'Critical': return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'High': return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
      case 'Medium': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
      default: return 'text-green-500 bg-green-500/10 border-green-500/20';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#02040a] selection:bg-accent/30 export-pdf">
      {/* 1. COVER PAGE SECTION */}
      <section className="min-h-screen flex flex-col justify-between p-12 md:p-24 relative overflow-hidden bg-background">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-accent/5 to-transparent pointer-events-none" />
        <div className="absolute top-[20%] right-[10%] w-[40rem] h-[40rem] bg-accent/10 rounded-full blur-[200px] pointer-events-none" />
        
        {/* Top Navbar Style Header */}
        <div className="flex justify-between items-start relative z-10">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-accent flex items-center justify-center rounded-lg shadow-lg shadow-accent/20">
              <Shield className="w-6 h-6 text-background" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-display font-bold tracking-tight uppercase">{BUSINESS_NAME}</span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Strategic Security Advisory</span>
            </div>
          </div>
          <div className="no-print">
             <Button variant="ghost" onClick={handlePrint} className="text-muted-foreground hover:text-foreground">
               <Download className="w-4 h-4 mr-2" /> PDF Version
             </Button>
          </div>
        </div>

        {/* Main Content of Cover */}
        <div className="relative z-10 space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="inline-flex items-center gap-2 text-accent/60 font-bold uppercase tracking-[0.3em] text-[9px] border-b border-accent/20 pb-2">
              Confidential Consulting Deliverable
            </div>
            <h1 className="text-5xl md:text-8xl font-display font-bold tracking-tight leading-tight text-foreground max-w-4xl">
              Compliance Risk <br /> 
              <span className="text-accent underline decoration-accent/20 underline-offset-8">Assessment Report</span>
            </h1>
            <p className="text-lg md:text-2xl text-muted-foreground font-normal max-w-3xl leading-relaxed mt-6">
              A comprehensive cybersecurity review and gap analysis <br className="hidden md:block" /> prepared for <span className="text-foreground font-semibold">{data.clientName}</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-12 pt-12 border-t border-white/10"
          >
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-accent">Document ID</span>
              <p className="font-mono text-sm">AG-{data.id.slice(0, 8).toUpperCase()}</p>
            </div>
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-accent">Issued Date</span>
              <p className="text-sm border-white/10">{new Date(data.generatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-accent">Consultant</span>
              <p className="text-sm">Strategic Advisory Team</p>
            </div>
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-accent">Status</span>
              <p className="text-sm flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                Verified Output
              </p>
            </div>
          </motion.div>
        </div>

        {/* Bottom Logo / Prompt */}
        <div className="flex justify-between items-end relative z-10 no-print">
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Scroll to proceed to executive findings</p>
          <motion.div 
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center"
          >
            <ChevronRight className="w-5 h-5 rotate-90 text-accent/50" />
          </motion.div>
        </div>
      </section>

      {/* 2. EXECUTIVE SUMMARY SECTION */}
      <section className="min-h-screen container mx-auto px-6 py-24 md:py-40 space-y-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-40">
            <span className="text-xs uppercase tracking-[0.5em] font-black text-accent block">Overview</span>
            <h2 className="text-4xl md:text-5xl font-display font-bold tracking-tight">Executive <br /> Perspective</h2>
            <div className="h-px w-24 bg-accent" />
            <p className="text-muted-foreground leading-relaxed">
              This assessment provides a definitive view of the current security posture, focusing on business risk rather than just technical vulnerabilities.
            </p>
          </div>
          
          <div className="lg:col-span-8 bg-secondary/10 border border-white/5 p-10 md:p-16 rounded-3xl relative overflow-hidden">
             <div className="space-y-10">
               <div className="prose prose-invert max-w-none">
                 <p className="text-xl md:text-2xl leading-relaxed text-foreground/90 italic">
                   "{data.executiveSummary}"
                 </p>
               </div>
               
               <div className="h-px w-full bg-white/5" />

               <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                 <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-accent" />
                      <h4 className="font-display font-bold text-base tracking-tight uppercase">Strategic Posture</h4>
                    </div>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                       {data.remediationRoadmap.immediate[0] || "Review of immediate critical gaps"}. Addressing these items is essential for maintaining operational stability and client trust.
                    </p>
                 </div>
                 <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-orange-500" />
                      <h4 className="font-display font-bold text-base tracking-tight uppercase">Executive Priority</h4>
                    </div>
                    <p className="text-sm leading-relaxed text-muted-foreground italic border-l-2 border-orange-500/30 pl-6">
                       A risk classification of <span className="text-orange-500 font-bold uppercase">{data.riskLevel}</span> requires immediate attention to prevent potential business interruption.
                    </p>
                 </div>
               </div>
             </div>
          </div>
        </div>

        {/* 3. RISK SCORE OVERVIEW */}
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <span className="text-[10px] uppercase tracking-[0.4em] font-bold text-muted-foreground">Current Security Standing</span>
            <h3 className="text-3xl font-display font-bold">Your Risk Profile</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Main Score Gauge */}
            <div className="bg-white/[0.02] border border-white/5 p-10 rounded-3xl flex flex-col items-center justify-center text-center space-y-6 md:col-span-1">
               <div className="relative w-48 h-48 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="transparent"
                    className="text-white/5"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="currentColor"
                    strokeWidth="10"
                    fill="transparent"
                    strokeDasharray={552}
                    strokeDashoffset={552 - (552 * data.riskScore) / 100}
                    strokeLinecap="round"
                    className={cn(
                      "transition-all duration-1000 ease-out",
                      data.riskScore > 70 ? 'text-red-500' : 'text-accent'
                    )}
                  />
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="text-6xl font-display font-bold tracking-tight">{data.riskScore}%</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-1">Risk Factor</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className={cn("px-6 py-2 rounded-lg font-bold text-[10px] uppercase tracking-widest inline-block border", getRiskColor(data.riskLevel))}>
                  {data.riskLevel} Level Risk
                </div>
                <p className="text-xs text-muted-foreground max-w-[180px] mx-auto leading-relaxed">
                  Consolidated risk score based on industry-standard security control maturity.
                </p>
              </div>
            </div>

            {/* Framework Breakdown */}
            <div className="md:col-span-2 bg-white/[0.02] border border-white/5 p-12 rounded-3xl grid grid-cols-1 md:grid-cols-2 gap-12">
               <div className="space-y-8">
                  <div className="space-y-1">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-accent">Benchmarks</span>
                    <h4 className="text-xl font-display font-bold tracking-tight">Compliance Readiness</h4>
                  </div>
                  <div className="space-y-6">
                    {Object.entries(data.frameworkScores || {}).map(([key, score]) => (
                      <div key={key} className="space-y-2">
                        <div className="flex justify-between items-end">
                          <span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">{key.toUpperCase()} Status</span>
                          <span className="text-sm font-bold font-mono text-foreground">{score}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                           <motion.div 
                             initial={{ width: 0 }}
                             whileInView={{ width: `${score}%` }}
                             transition={{ duration: 1, ease: "easeOut" }}
                             className="h-full bg-accent rounded-full"
                           />
                        </div>
                      </div>
                    ))}
                  </div>
               </div>
               <div className="bg-accent/5 p-8 rounded-2xl border border-accent/10 flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-accent" />
                    </div>
                    <h5 className="text-lg font-display font-bold tracking-tight">Advisory Note</h5>
                    <p className="text-sm leading-relaxed text-muted-foreground italic">
                      "{data.advisoryNote}"
                    </p>
                  </div>
                  <div className="pt-8 flex items-center gap-4 mt-8 border-t border-accent/10">
                    <div className="w-10 h-10 rounded-full bg-accent text-background flex items-center justify-center font-bold text-xs">FA</div>
                    <div className="flex flex-col">
                       <span className="text-xs font-bold text-foreground">Fabiyan Anik</span>
                       <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Agrandintl Advisor</span>
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>

        {/* 4. KEY FINDINGS SECTION */}
        <div className="space-y-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-4">
               <span className="text-xs uppercase tracking-[0.5em] font-black text-accent">Key Findings</span>
               <h3 className="text-4xl font-display font-bold tracking-tight">What We <br /> Found</h3>
            </div>
            <div className="flex items-center gap-4 text-xs font-bold text-muted-foreground uppercase tracking-widest">
               <span>{data.findings.length} Items Identified</span>
               <div className="h-px w-12 bg-white/10" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {data.findings.map((finding, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className="bg-white/[0.02] border border-white/5 p-10 rounded-2xl flex flex-col h-full hover:border-accent/20 transition-all duration-300">
                  <div className="flex justify-between items-center mb-8">
                    <div className={cn("px-4 py-1 rounded-lg text-[9px] uppercase tracking-widest font-bold border", getRiskColor(finding.severity))}>
                      {finding.severity} Severity
                    </div>
                    <span className="text-xs font-mono opacity-30">FE-0{idx + 1}</span>
                  </div>
                  
                  <h4 className="text-2xl font-display font-bold mb-4 tracking-tight leading-tight">{finding.title}</h4>
                  <p className="text-muted-foreground leading-relaxed mb-10 flex-1 text-sm font-normal">
                    {finding.description}
                  </p>

                  <div className="pt-8 border-t border-white/5">
                    <div className="flex gap-4 items-start">
                       <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                         <Target className="w-4 h-4 text-accent" />
                       </div>
                       <div className="space-y-1">
                          <span className="text-[10px] uppercase font-bold text-accent tracking-widest">Operational Risk</span>
                          <p className="text-[13px] leading-relaxed text-foreground/80 font-medium">{finding.impact}</p>
                       </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 5. BUSINESS IMPACT CALLOUTS */}
        <div className="bg-accent/[0.02] border border-white/5 rounded-[4rem] p-12 md:p-24 space-y-16 relative overflow-hidden">
           <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/5 blur-[120px] rounded-full -mb-48 -mr-48" />
           
           <div className="text-center max-w-3xl mx-auto space-y-4">
              <span className="text-xs uppercase tracking-[0.4em] font-bold text-accent">The Business Impact</span>
              <h3 className="text-4xl font-display font-bold">What's At Stake</h3>
              <p className="text-muted-foreground">This is how these security gaps hit your bottom line if left unaddressed.</p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10">
              {[
                { title: 'Financial', desc: data.businessImpact.financial, icon: Target },
                { title: 'Compliance', desc: data.businessImpact.compliance, icon: ShieldCheck },
                { title: 'Reputational', desc: data.businessImpact.reputational, icon: Globe },
                { title: 'Operational', desc: data.businessImpact.operational, icon: Zap }
              ].map((item, i) => (
                <div key={i} className="space-y-6 text-center md:text-left">
                   <div className="w-12 h-12 bg-accent/5 border border-accent/10 rounded-2xl flex items-center justify-center mx-auto md:mx-0">
                     <item.icon className="w-6 h-6 text-accent" />
                   </div>
                   <div className="space-y-2">
                     <h5 className="font-bold text-lg">{item.title}</h5>
                     <p className="text-xs leading-[1.8] text-muted-foreground">{item.desc}</p>
                   </div>
                </div>
              ))}
           </div>
        </div>

        {/* 6. REMEDIATION ROADMAP */}
        <div className="space-y-16">
          <div className="text-center space-y-4">
             <span className="text-xs uppercase tracking-[0.4em] font-black text-accent">Our Plan Forward</span>
             <h3 className="text-4xl font-display font-bold tracking-tight">The Roadmap</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {[
              { 
                phase: 'Critical Remediation', 
                time: 'Immediate', 
                items: data.remediationRoadmap.immediate,
                color: 'text-red-500'
              },
              { 
                phase: 'Structural Hardening', 
                time: '30-60 Days', 
                items: data.remediationRoadmap.shortTerm,
                color: 'text-accent'
              },
              { 
                phase: 'Governance Maturation', 
                time: '90 Days+', 
                items: data.remediationRoadmap.longTerm,
                color: 'text-green-500'
              }
            ].map((step, i) => (
              <motion.div 
                key={i}
                className="bg-white/[0.02] border border-white/5 p-10 rounded-2xl flex flex-col h-full"
              >
                <div className="flex justify-between items-start mb-10">
                   <div className="space-y-1">
                      <span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">Phase 0{i + 1}</span>
                      <h4 className={cn("text-xl font-display font-bold tracking-tight", step.color)}>{step.phase}</h4>
                   </div>
                   <div className="px-3 py-1 rounded-lg bg-white/5 border border-white/5 text-[9px] font-bold uppercase tracking-widest">{step.time}</div>
                </div>
                
                <ul className="space-y-5 flex-1">
                  {step.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm font-normal leading-relaxed text-muted-foreground">
                      <CheckCircle2 className={cn("w-4 h-4 shrink-0 mt-0.5 opacity-40", step.color)} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 7. FRAMEWORK ALIGNMENT SECTION */}
        <div className="space-y-16">
          <div className="text-center space-y-4">
             <span className="text-xs uppercase tracking-[0.4em] font-black text-accent">Compliance Overview</span>
             <h3 className="text-4xl font-display font-bold tracking-tight">Why Compliance Matters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { title: 'PCI DSS', category: 'Payment Security', details: data.frameworkAlignment.pciDetails },
              { title: 'HIPAA', category: 'Health Privacy', details: data.frameworkAlignment.hipaaDetails },
              { title: 'SOC 2', category: 'Service Controls', details: data.frameworkAlignment.soc2Details }
            ].map((framework, i) => (
              <div key={i} className="space-y-6">
                <div className="space-y-2">
                  <Badge variant="outline" className="border-accent/20 text-accent uppercase tracking-widest text-[9px]">{framework.category}</Badge>
                  <h4 className="text-2xl font-display font-bold">{framework.title}</h4>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">{framework.details}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 8. NEXT STEPS / UPSELL */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-accent/5 border border-accent/10 p-12 md:p-16 rounded-3xl relative overflow-hidden no-print"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
             <div className="lg:col-span-8 space-y-6">
                <div className="inline-flex items-center gap-2 text-accent text-[10px] font-bold uppercase tracking-widest bg-accent/10 px-3 py-1 rounded-md">
                   Strategic Implementation support
                </div>
                <div className="space-y-4">
                  <h2 className="text-3xl md:text-5xl font-display font-bold leading-tight">Fortify Your Operational <br /> <span className="text-accent underline decoration-accent/20 underline-offset-4">Security Infrastructure.</span></h2>
                  <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl">
                    Our team provides hands-on engineering and strategic support to mitigate these risks and prepare your organization for upcoming audits.
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-x-10 gap-y-3 pt-6 border-t border-white/5">
                  {data.upsellServices.map((service, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                      <CheckCircle2 className="w-3 h-3 text-accent" />
                      {service}
                    </div>
                  ))}
                </div>
             </div>
 
             <div className="lg:col-span-4 flex flex-col gap-4 w-full">
                <Button asChild size="lg" className="bg-accent text-background rounded-xl h-16 px-8 font-bold uppercase tracking-widest text-xs hover:bg-accent/90 transition-colors w-full">
                  <Link to="/contact">Schedule Implementation Call</Link>
                </Button>
                <div className="bg-background/20 p-6 rounded-xl border border-white/5 flex items-center justify-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent text-background flex items-center justify-center font-bold text-xs">FA</div>
                  <div className="flex flex-col items-start leading-tight">
                      <span className="text-sm font-bold">Fabiyan Anik</span>
                      <span className="text-[10px] uppercase tracking-widest opacity-60">Managing Advisor</span>
                  </div>
                </div>
             </div>
          </div>
        </motion.div>

        {/* 8. FOOTER / CLOSING */}
        <footer className="pt-24 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-12 items-end">
          <div className="space-y-6">
             <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-accent/50" />
                </div>
                <span className="text-sm font-display font-bold uppercase tracking-widest">{BUSINESS_NAME}</span>
             </div>
             <p className="text-xs text-muted-foreground uppercase tracking-widest leading-relaxed font-bold">
               Executive Risk Report <br />
               Internal Reference: #{data.id.slice(0, 10)}
             </p>
          </div>

          <div className="space-y-4 text-center md:text-left">
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-accent block">Region of Service</span>
            <p className="text-sm text-foreground/80 font-bold">Washington DC • Virginia • Maryland</p>
          </div>

          <div className="flex flex-col items-center md:items-end gap-2 text-right">
             <a href={`mailto:${COMPANY_DETAILS.email}`} className="text-sm font-bold hover:text-accent transition-colors">{COMPANY_DETAILS.email}</a>
             <p className="text-[10px] tracking-widest font-black uppercase text-accent">{COMPANY_DETAILS.phone}</p>
             <p className="text-[9px] text-muted-foreground opacity-50 pt-4 uppercase tracking-[0.2em]">© {new Date().getFullYear()} Agrandintl Professional Services. Confidential.</p>
          </div>
        </footer>
      </section>

      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; color: black !important; }
          .min-h-screen { min-height: auto !important; height: auto !important; page-break-after: always !important; }
          .glass-card { border: 1px solid #111 !important; background: transparent !important; color: black !important; padding: 2.5rem !important; }
          .container { max-width: 100% !important; margin: 0 !important; width: 100% !important; padding: 2cm !important; }
          .pt-32, .pt-12 { padding-top: 0 !important; }
          h1, h2, h3, h4, .text-accent { color: black !important; }
          .text-reveal { -webkit-text-fill-color: black !important; background: none !important; }
          .export-pdf { padding: 0 !important; }
          .p-24, .p-12, .py-40 { padding: 2cm !important; }
          section { border: none !important; box-shadow: none !important; }
          .bg-background, .bg-[#02040a] { background: white !important; }
          .text-muted-foreground { color: #555 !important; }
          .border-white\\/10 { border-color: #eee !important; }
          .bg-accent\\/10 { display: none !important; }
          @page { size: A4; margin: 0; }
        }
      `}} />
    </div>
  );
}
